// src/pages/FundPlanner/components/SecurityMaster.jsx
import React, { useCallback, useMemo, useState } from "react";
import SectionCard from "components/SectionCard";
import ExcelGrid from "components/ExcelGrid";
import { supabase } from "services/supabaseClient";

function normalizeCusip(value) {
  return String(value ?? "")
    .trim()
    .toUpperCase()
    .replace(/[-\s]/g, "");
}

export default function SecurityMaster() {
  const [rows, setRows] = useState([]);
  const [viewMode, setViewMode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [lastError, setLastError] = useState("");

  const columns = useMemo(() => {
    const base = [
      {
        key: "investment_type",
        label: "Investment Type",
        editable: false,
        width: "min-w-[180px]",
      },
      {
        key: "cusip",
        label: "CUSIP",
        editable: false,
        width: "min-w-[160px]",
      },
      {
        key: "position_count",
        label: "Position Count",
        editable: false,
        width: "min-w-[140px]",
      },
      {
        key: "org_count",
        label: "Org Count",
        editable: false,
        width: "min-w-[120px]",
      },
    ];

    if (viewMode === "all") {
      return [
        ...base,
        {
          key: "in_security_master",
          label: "In Security Master",
          editable: false,
          width: "min-w-[170px]",
        },
      ];
    }

    return base;
  }, [viewMode]);

  const loadMissing = useCallback(async () => {
    setIsLoading(true);
    setLastError("");

    try {
      const { data, error } = await supabase.rpc("fp_security_master_missing");

      if (error) throw error;

      const nextRows = (data || []).map((row) => ({
        investment_type: row.investment_type || "",
        cusip: normalizeCusip(row.cusip),
        position_count: row.position_count ?? 0,
        org_count: row.org_count ?? 0,
      }));

      setRows(nextRows);
      setViewMode("missing");
    } catch (error) {
      console.error("Error loading missing Security Master CUSIPs:", error);
      setLastError(error?.message || "Unable to load missing CUSIPs.");
      setRows([]);
      setViewMode("missing");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const loadAll = useCallback(async () => {
    setIsLoading(true);
    setLastError("");

    try {
      const { data, error } = await supabase.rpc("fp_security_master_all");

      if (error) throw error;

      const nextRows = (data || []).map((row) => ({
        investment_type: row.investment_type || "",
        cusip: normalizeCusip(row.cusip),
        position_count: row.position_count ?? 0,
        org_count: row.org_count ?? 0,
        in_security_master: row.in_security_master ? "Yes" : "",
      }));

      setRows(nextRows);
      setViewMode("all");
    } catch (error) {
      console.error("Error loading all position CUSIPs:", error);
      setLastError(error?.message || "Unable to load all position CUSIPs.");
      setRows([]);
      setViewMode("all");
    } finally {
      setIsLoading(false);
    }
  }, []);

  const titleText = useMemo(() => {
    if (viewMode === "missing") return "CUSIPs in Positions but Missing from Security Master";
    if (viewMode === "all") return "All Unique CUSIPs in Positions";
    return "Security Master Review";
  }, [viewMode]);

  const helperText = useMemo(() => {
    if (viewMode === "missing") {
      return "Shows unique CUSIPs found in fp_positions that do not exist in fp_security_master.";
    }
    if (viewMode === "all") {
      return "Shows all unique normalized CUSIPs found in fp_positions, whether or not they already exist in fp_security_master.";
    }
    return "Choose one of the actions below to generate the table.";
  }, [viewMode]);

  const rowCountLabel = useMemo(() => {
    if (isLoading) return "Loading…";
    const count = rows.length;
    return `${count} ${count === 1 ? "CUSIP" : "CUSIPs"}`;
  }, [isLoading, rows.length]);

  return (
    <div className="space-y-6">
      <SectionCard title="Security Master">
        <div className="space-y-4">
          <div className="text-sm text-muted">{helperText}</div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="text-sm text-muted">{rowCountLabel}</div>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className="btn btn-primary"
                onClick={loadMissing}
                disabled={isLoading}
              >
                {isLoading && viewMode === "missing"
                  ? "Loading Missing..."
                  : "Get Missing"}
              </button>

              <button
                type="button"
                className="btn btn-primary"
                onClick={loadAll}
                disabled={isLoading}
              >
                {isLoading && viewMode === "all"
                  ? "Loading All..."
                  : "Get All"}
              </button>
            </div>
          </div>

          {lastError ? (
            <div className="text-sm text-red-600">{lastError}</div>
          ) : null}

          <div className="text-sm text-app font-medium">{titleText}</div>

          <ExcelGrid columns={columns} rows={rows} onChange={setRows} />
        </div>
      </SectionCard>
    </div>
  );
}